import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { streamAI } from "@/lib/ai";
import { printDocument } from "@/lib/print";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Printer, Upload, FileText, Image as ImageIcon, Trash2, Eye, Stethoscope, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { StaffPatientChat, CareTeamPanel } from "@/components/opd/PatientChat";

const CATEGORIES = [
  { key: "present_reports", label: "Present / Recent Reports" },
  { key: "past_hospitalisation", label: "Past Hospitalisation Records" },
];

export function printCase(c, patient) {
  const rows = (c.prescription || []).map((r) => `<tr><td>${r.drug || ""}</td><td>${r.dose || ""}</td><td>${r.frequency || ""}</td><td>${r.duration || ""}</td><td>${r.instructions || ""}</td></tr>`).join("");
  printDocument(`Prescription ${c.case_no || ""}`, `
    <div class="row"><div><h1>Prescription</h1><div class="muted">${c.case_no || ""} · ${c.visit_date || (c.created_at || "").slice(0, 10)}</div></div>
    <div class="muted" style="text-align:right">Dr. ${c.doctor_name || "—"}</div></div>
    <hr class="hr"/>
    <div class="row"><div><strong>${patient?.name || ""}</strong><div class="muted">${patient?.uhid || ""} · ${patient?.gender || ""}, ${patient?.age || ""}y</div></div></div>
    ${c.chief_complaint ? `<div class="sec"><h3>Chief complaint</h3>${c.chief_complaint}</div>` : ""}
    <div class="sec"><h3>Diagnosis</h3>${(c.diagnosis || []).map((d) => `<span class="tag">${d}</span>`).join("") || "—"}</div>
    <div class="sec"><h3>Rx</h3><table><thead><tr><th>Drug</th><th>Dose</th><th>Frequency</th><th>Duration</th><th>Instructions</th></tr></thead><tbody>${rows || '<tr><td colspan=5>No medicines</td></tr>'}</tbody></table></div>
    ${c.notes ? `<div class="sec"><h3>Notes</h3>${c.notes}</div>` : ""}
    ${c.follow_up_date ? `<div class="sec"><h3>Follow-up</h3>${c.follow_up_date}${c.follow_up_time ? ` at ${c.follow_up_time}` : ""} — ${c.follow_up_advice || ""}</div>` : ""}`);
}

export function CaseCard({ c, patient }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-md" data-testid={`case-${c.case_no || c.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left">
        <div>
          <div className="font-medium text-slate-800 flex items-center gap-2"><Stethoscope className="h-4 w-4 text-blue-600" /> {c.case_no || "Case"} · {c.visit_date || (c.created_at || "").slice(0, 10)}</div>
          <div className="text-xs text-slate-500 mt-0.5">{(c.diagnosis || []).join(", ") || "No diagnosis"} · Dr. {c.doctor_name || "—"}</div>
        </div>
        <span className="text-xs text-slate-400">{(c.prescription || []).length} med(s)</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-3">
          {c.chief_complaint && <div><div className="text-[11px] uppercase text-slate-400 font-mono">Complaint</div><div className="text-sm text-slate-700">{c.chief_complaint}</div></div>}
          <div>
            <div className="text-[11px] uppercase text-slate-400 font-mono mb-1">Prescription</div>
            {(c.prescription || []).length === 0 ? <div className="text-sm text-slate-400">No medicines</div> : (
              <div className="border border-slate-100 rounded-md divide-y divide-slate-100">
                {c.prescription.map((r, i) => (
                  <div key={i} className="px-3 py-2">
                    <div className="text-sm font-medium text-slate-800">{r.drug}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {[r.dose, r.frequency, r.duration].filter(Boolean).join(" · ")}
                      {r.instructions && <span className="italic text-slate-400">{[r.dose, r.frequency, r.duration].filter(Boolean).length ? " — " : ""}{r.instructions}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {c.follow_up_date && <div className="text-sm text-amber-700">Follow-up: {c.follow_up_date}{c.follow_up_time ? ` at ${c.follow_up_time}` : ""} {c.follow_up_advice ? `— ${c.follow_up_advice}` : ""}</div>}
          <Button size="sm" variant="outline" onClick={() => printCase(c, patient)} data-testid={`case-print-${c.case_no || c.id}`}><Printer className="h-4 w-4 mr-1.5" /> Print prescription</Button>
        </div>
      )}
    </div>
  );
}

export function MrdManager({ docs, loading, canUpload, canDeleteDoc, onUpload, onDelete, onView, uploading }) {
  const [category, setCategory] = useState("present_reports");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);

  const submit = async () => {
    if (!file) { toast.error("Choose a PDF or image file first"); return; }
    await onUpload({ file, category, title });
    setFile(null); setTitle("");
  };

  return (
    <div className="space-y-5">
      {canUpload && (
        <div className="border border-slate-200 rounded-md p-4 bg-slate-50/60 space-y-3" data-testid="mrd-upload-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Category</Label>
              <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="mrd-category-select">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Title (optional)</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Discharge summary 2024" data-testid="mrd-title-input" /></div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="mrd-file-input" />
            <Button size="sm" onClick={submit} disabled={uploading || !file} className="bg-blue-700 hover:bg-blue-800" data-testid="mrd-upload-btn">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Upload</>}</Button>
          </div>
          <div className="text-[11px] text-slate-400">PDF, JPG or PNG · up to 10 MB. This is a lifetime record and stays with the patient.</div>
        </div>
      )}
      {loading ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
        <div className="space-y-5">
          {CATEGORIES.map((cat) => {
            const items = (docs || []).filter((d) => d.category === cat.key);
            return (
              <div key={cat.key} data-testid={`mrd-group-${cat.key}`}>
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">{cat.label} ({items.length})</div>
                <div className="space-y-2">
                  {items.map((d) => {
                    const isImg = (d.content_type || "").startsWith("image/") && !(d.filename || "").toLowerCase().endsWith(".pdf");
                    return (
                      <div key={d.id} className="flex items-center gap-3 border border-slate-100 rounded-md px-3 py-2.5" data-testid={`mrd-doc-${d.id}`}>
                        <span className="h-8 w-8 rounded bg-slate-100 grid place-items-center text-slate-500 shrink-0">{isImg ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800 truncate">{d.title || d.filename}</div>
                          <div className="text-[11px] text-slate-400">{(d.uploader_type === "patient" ? "Patient" : d.uploaded_by || "Staff")} · {(d.uploaded_at || "").slice(0, 10)} · {Math.round((d.size || 0) / 1024)} KB</div>
                        </div>
                        <Button size="icon" variant="ghost" onClick={() => onView(d)} data-testid={`mrd-view-${d.id}`}><Eye className="h-4 w-4" /></Button>
                        {canDeleteDoc(d) && <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => onDelete(d)} data-testid={`mrd-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    );
                  })}
                  {items.length === 0 && <div className="text-sm text-slate-400">No documents.</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PatientVaultPanel({ patientId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/opd/patients/${patientId}/vault`).then((r) => setData(r.data)).catch(() => setData({ linked: false, documents: [], medications: [], notes: [] }));
  }, [patientId]);
  if (!data) return <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  if (!data.linked) return <div className="text-center py-12 text-slate-400 text-sm" data-testid="patient-vault-panel">This patient has not connected a Mediva Health account, so there is no shared vault.</div>;
  const viewDoc = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/opd/patients/${patientId}/vault/documents/${id}/file`, "_blank", "noopener,noreferrer");
  return (
    <div className="space-y-4" data-testid="patient-vault-panel">
      <p className="text-xs text-indigo-600">Shared from the patient's personal Mediva Health vault (read-only).</p>
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">Documents</div>
        {(data.documents || []).length === 0 ? <p className="text-sm text-slate-400">No documents.</p> : (
          <div className="space-y-1.5">
            {data.documents.map((d) => (
              <div key={d.id} className="flex items-center gap-2 border border-slate-100 rounded-md px-3 py-2" data-testid={`vault-doc-${d.id}`}>
                <FileText className="h-4 w-4 text-indigo-600 shrink-0" />
                <span className="flex-1 text-sm text-slate-700 truncate">{d.title}</span>
                <Button variant="outline" size="sm" onClick={() => viewDoc(d.id)} data-testid={`vault-doc-view-${d.id}`}><Eye className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">Medications</div>
        {(data.medications || []).length === 0 ? <p className="text-sm text-slate-400">None listed.</p> : (
          <div className="space-y-1">
            {data.medications.map((m) => <div key={m.id} className="text-sm text-slate-700 border border-slate-100 rounded-md px-3 py-1.5">{m.name} <span className="text-xs text-slate-400">{[m.dose, m.frequency].filter(Boolean).join(" · ")}</span></div>)}
          </div>
        )}
      </div>
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1.5">Notes</div>
        {(data.notes || []).length === 0 ? <p className="text-sm text-slate-400">None.</p> : (
          <div className="space-y-1">
            {data.notes.map((n) => <div key={n.id} className="text-sm text-slate-700 border border-slate-100 rounded-md px-3 py-1.5">{n.title ? <b>{n.title}: </b> : ""}{n.text}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}

export function AiSummaryPanel({ patientId }) {
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [started, setStarted] = useState(false);
  const run = async () => {
    setStreaming(true); setText(""); setStarted(true);
    try {
      await streamAI(`/ai/patient-summary/${patientId}`, { onChunk: (t) => setText((s) => s + t) });
    } catch { toast.error("Could not generate the summary. Please try again."); }
    finally { setStreaming(false); }
  };
  return (
    <div className="space-y-3" data-testid="ai-summary-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-indigo-700"><Sparkles className="h-4 w-4" /><span className="text-[11px] font-mono uppercase tracking-wider">AI History Summary</span></div>
        <Button size="sm" onClick={run} disabled={streaming} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ai-summary-btn">
          {streaming ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Summarizing…</> : <><Sparkles className="h-4 w-4 mr-1.5" /> {started ? "Regenerate" : "Summarize with AI"}</>}
        </Button>
      </div>
      <p className="text-sm text-slate-500">A quick, read-only briefing from this patient's cases, prescriptions and records. Not stored as a medical record.</p>
      {!started ? (
        <div className="border border-dashed border-indigo-200 bg-indigo-50/40 rounded-md py-10 text-center text-sm text-slate-500">Tap "Summarize with AI" to generate a briefing.</div>
      ) : (
        <div className="border border-indigo-200 bg-indigo-50/40 rounded-md p-4">
          <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="ai-summary-text">{text}{streaming && <span className="inline-block w-2 h-4 bg-indigo-400 ml-0.5 animate-pulse align-middle" />}</div>
          {!streaming && text && <p className="text-[11px] text-slate-500 mt-3">AI-generated summary — verify against the full record before clinical decisions.</p>}
        </div>
      )}
    </div>
  );
}

export function PatientRecordDialog({ open, onOpenChange, patientId, has }) {  const [patient, setPatient] = useState(null);
  const [cases, setCases] = useState(null);
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadDocs = () => api.get(`/opd/patients/${patientId}/mrd`).then((r) => setDocs(r.data)).catch(() => setDocs([]));

  useEffect(() => {
    if (open && patientId) {
      setPatient(null); setCases(null); setDocs(null);
      api.get(`/opd/patients/${patientId}`).then((r) => setPatient(r.data)).catch(() => setPatient(false));
      api.get(`/opd/patients/${patientId}/cases`).then((r) => setCases(r.data)).catch(() => setCases([]));
      loadDocs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientId]);

  const doUpload = async ({ file, category, title }) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("category", category); if (title) fd.append("title", title);
      await api.post(`/opd/patients/${patientId}/mrd`, fd);
      toast.success("Document uploaded");
      loadDocs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setUploading(false); }
  };
  const doDelete = async (d) => {
    if (!window.confirm("Delete this document permanently?")) return;
    try { await api.delete(`/opd/mrd/${d.id}`); toast.success("Deleted"); loadDocs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doView = async (d) => {
    try {
      const res = await api.get(`/opd/mrd/${d.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener,noreferrer");
    } catch { toast.error("Unable to open document"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="patient-record-dialog">
        <DialogHeader>
          <DialogTitle>{patient?.name || "Patient"} · <span className="font-mono text-sm text-blue-700">{patient?.uhid}</span></DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="overview" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="w-full max-w-full justify-start overflow-x-auto flex-nowrap [&>*]:shrink-0" data-testid="record-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="cases" data-testid="tab-cases">Prescriptions / Cases</TabsTrigger>
            <TabsTrigger value="mrd" data-testid="tab-mrd">Medical Records</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai-summary">AI Summary</TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat">Chat</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">Care Team</TabsTrigger>
            <TabsTrigger value="vault" data-testid="tab-patient-vault">Patient Vault</TabsTrigger>
          </TabsList>
          <div className="overflow-y-auto thin-scroll pt-4 flex-1">
            <TabsContent value="overview">
              {patient && (
                <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                  <div className="text-slate-500">Gender / Age</div><div className="text-slate-800">{patient.gender}, {patient.age ?? "—"}y</div>
                  <div className="text-slate-500">Mobile</div><div className="text-slate-800">{patient.mobile || "—"}</div>
                  <div className="text-slate-500">Blood group</div><div className="text-slate-800">{patient.blood_group || "—"}</div>
                  <div className="text-slate-500">Allergies</div><div className="text-red-600 font-medium">{patient.allergies || "None recorded"}</div>
                  <div className="text-slate-500">Referred by</div><div className="text-slate-800">{patient.referred_by || "—"}</div>
                  <div className="text-slate-500">Address</div><div className="text-slate-800">{patient.address || "—"}</div>
                </div>
              )}
            </TabsContent>
            <TabsContent value="cases">
              {cases === null ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
                <div className="space-y-2">
                  {cases.map((c) => <CaseCard key={c.id} c={c} patient={patient} />)}
                  {cases.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No consultation cases yet. A case is created after each OPD consultation is saved.</div>}
                </div>
              )}
            </TabsContent>
            <TabsContent value="mrd">
              <MrdManager docs={docs} loading={docs === null} uploading={uploading}
                canUpload={has ? has("patient:edit") : true}
                canDeleteDoc={() => (has ? has("patient:edit") : true)}
                onUpload={doUpload} onDelete={doDelete} onView={doView} />
            </TabsContent>
            <TabsContent value="ai">
              {patientId && <AiSummaryPanel patientId={patientId} />}
            </TabsContent>
            <TabsContent value="chat">
              {patientId && <StaffPatientChat patientId={patientId} />}
            </TabsContent>
            <TabsContent value="team">
              {patientId && <CareTeamPanel patientId={patientId} />}
            </TabsContent>
            <TabsContent value="vault">
              {patientId && <PatientVaultPanel patientId={patientId} />}
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
